<template>
  <div class="kpi-board">
    <div
      v-for="item in items"
      :key="item.label"
      class="kpi-board__item"
      :class="`kpi-board__item--${item.tone || 'primary'}`"
      :style="itemStyle"
    >
      <span class="kpi-board__icon" aria-hidden="true">
        <i :class="item.icon" aria-hidden="true" />
      </span>
      <div class="kpi-board__text">
        <span
          class="kpi-board__num"
          :class="{ 'is-danger': (item.tone || 'primary') === 'danger' }"
          >{{ item.value }}</span
        >
        <span class="kpi-board__label">{{ item.label }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';

export interface KpiItem {
  value: number | string;
  label: string;
  icon: string;
  tone?: 'primary' | 'success' | 'warning' | 'danger' | 'info';
}

export default Vue.extend({
  name: 'KpiBoard',
  props: {
    items: { type: Array as () => KpiItem[], default: () => [] },
    columns: { type: Number, default: 0 },
    /** 为 true 时 KPI 固定单行均分，不换行 */
    singleRow: { type: Boolean, default: true },
  },
  computed: {
    itemWidth(): string {
      const count = this.columns || this.items.length || 1;
      return 100 / count + '%';
    },
    itemStyle(): Record<string, string> {
      if (this.singleRow) return {};
      return { width: this.itemWidth };
    },
  },
});
</script>

<style lang="scss" scoped>
.kpi-board {
  display: flex;
  flex-wrap: nowrap;
  margin: 0 -4px;

  &__item {
    display: flex;
    flex: 1 1 0;
    box-sizing: border-box;
    align-items: center;
    justify-content: space-between;
    min-width: 0;
    margin: 0 4px;
    padding: 10px 12px;
    overflow: hidden;
    border: 1px solid $color-border;
    border-radius: $border-radius-base;
    background: $color-surface;
  }

  &__icon {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 8px;
  }

  &__text {
    min-width: 0;
    text-align: right;
  }

  &__num {
    display: block;
    max-width: 100%;
    overflow: hidden;
    font-size: 26px;
    font-weight: 600;
    line-height: 1;
    text-overflow: ellipsis;

    &.is-danger {
      color: $color-danger;
    }
  }

  &__label {
    display: block;
    max-width: 100%;
    margin-top: 4px;
    overflow: hidden;
    font-size: 12px;
    line-height: 1.2;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: $color-text-secondary;
  }

  &__item--primary &__icon {
    background: $color-primary-light;
    color: $color-primary;
  }

  &__item--success &__icon {
    background: #e8f6ef;
    color: $color-success;
  }

  &__item--warning &__icon {
    background: #fdf3e8;
    color: $color-warning;
  }

  &__item--danger &__icon {
    background: #fdeeee;
    color: $color-danger;
  }

  &__item--info &__icon {
    background: $color-surface-muted;
    color: $color-info;
  }

  @media (max-width: $breakpoint-mobile) {
    &__item {
      min-height: 64px;
      padding: 10px 12px;

      .kpi-board__num {
        font-size: 22px;
      }

      .kpi-board__label {
        font-size: 11px;
      }
    }
  }
}
</style>
